'use client';

import { Button } from '@app/app/_components/Button/Button';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Input } from '@app/app/_components/Input/Input';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { PurchaseLifeInsuranceFormData } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { saveProgressWithEmail } from '@app/service/eshop/life/actions/saveProgressWithEmail';
import { LifePackage } from '@app/domain/model/eshop/life/LifePackage';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    data: PurchaseLifeInsuranceFormData;
    packages: LifePackage[];
}

export function SaveProgressModal({ open, onClose, onSuccess, data, packages }: Props) {
    const t = useTranslations();
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState<ViolationError>();

    const handleSubmit = async () => {
        const formData = new FormData();
        formData.append('email', email);

        const result = await saveProgressWithEmail(data, packages, { status: FormStatus.Pending }, formData);

        if (result.status === FormStatus.Success) {
            onSuccess();
        } else if (result.violations?.email) {
            setEmailError(result.violations.email);
        }
    };

    const onEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    }, []);

    if (!open) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                className="fixed inset-0 z-30 bg-black opacity-45"
                onClick={onClose}
                aria-label={t('general.close')}
            />

            <div className="fixed inset-0 z-50 overflow-y-auto bg-neutral-50 md:flex md:items-center md:justify-center md:p-8">
                <div className="flex min-h-full w-full flex-col bg-neutral-50 md:min-w-dialog md:max-w-dialog">
                    <div className="mb-4 flex items-start justify-between px-4 py-4">
                        <Heading level="h2" className="flex-1 pt-1">
                            {t('eshop.life.saveAndContinueLater')}
                        </Heading>
                        <GhostButton onClick={onClose} className="flex-shrink-0">
                            <CloseOutlinedCircleIcon className="size-8" />
                        </GhostButton>
                    </div>

                    <div className="mb-6 px-4">
                        <Paragraph className="mb-4">{t('eshop.life.saveProgressDescription')}</Paragraph>

                        <Input
                            name="email"
                            label={t('eshop.life.emailAddress')}
                            placeholder={t('profile.emailPlaceholder')}
                            type="email"
                            value={email}
                            onChange={onEmailChange}
                            error={emailError}
                        />
                    </div>

                    <div className="px-4 pb-6">
                        <Button className="w-full" onClick={handleSubmit}>
                            {t('general.save')}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
